import React, { FC, memo, useState } from "react";
import { Input, PasswordToggle, Textarea } from "@evergis/ui";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";

import { InputContainer } from "components/styled/forms";
import { FormDescription } from "./styled";

import { usePasswordHelpText } from "../../hooks/usePasswordHelpText";
import { useSignUpFormInputs } from "./useSignUpFormInputs";
import { useRegFormSettings } from "../../hooks/useRegFormSettings";

import { ISignUpProps } from "./types";

const isValidEmail = (value: string | undefined) => {
  const validator = Yup.string().email();
  let isEmail = true;

  try {
    validator.validateSync(value);
  } catch {
    isEmail = false;
  }

  return isEmail;
};

// eslint-disable-next-line max-lines-per-function
const FormInputsComponent: FC<ISignUpProps> = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  isUsernameUnique,
  setIsUsernameUnique,
  setIsEmailUnique,
  isEmailUnique,
}) => {
  const { t } = useTranslation();
  const { email, password1, password2, username, familyName, name, surname, company, phone, goals, required } =
    values || {};
  const [isPass1Visible, setIsPass1Visible] = useState(false);
  const [isPass2Visible, setIsPass2Visible] = useState(false);
  const [isPass2Changed, setIsPass2Changed] = useState(false);
  const regForm = useRegFormSettings();

  const { getPassword1HelpText, getPassword2HelpText } = usePasswordHelpText({
    errors,
    touched,
    isPass2Changed,
  });

  const { checkUsername, hasLoginError, getEmailHelpText, getUsernameHelpText, checkEmail, hasEmailError } =
    useSignUpFormInputs({
      username: username as string,
      errors,
      touched,
      isUsernameUnique,
      setIsUsernameUnique,
      email: email as string,
      isEmailUnique,
      setIsEmailUnique,
    });

  return (
    <>
      {regForm.signupByInvite && <FormDescription>{t("inviteDescription")}</FormDescription>}
      <InputContainer>
        <Input
          type="text"
          id="username"
          name="username"
          onChange={(e) => {
            handleChange && handleChange(e);
            checkUsername(e);
          }}
          value={username}
          label={regForm.login}
          help={getUsernameHelpText()}
          width={"100%"}
          required={required?.username}
          onBlur={handleBlur}
          error={hasLoginError}
          success={isUsernameUnique && !!username}
          autoComplete="username"
        />
      </InputContainer>
      <InputContainer>
        <Input
          type="text"
          id="email"
          name="email"
          onChange={(e) => {
            handleChange && handleChange(e);
            isValidEmail(e.target.value) && checkEmail(e);
          }}
          value={email}
          label={regForm.email}
          help={getEmailHelpText()}
          width={"100%"}
          error={hasEmailError}
          required={required?.email}
          onBlur={handleBlur}
          autoComplete="email"
        />
      </InputContainer>
      {!regForm.signupByInvite ? (
        <>
          <InputContainer>
            <Input
              type="text"
              id="familyName"
              name="familyName"
              onChange={(e) => {
                handleChange && handleChange(e);
              }}
              value={familyName}
              label={regForm.familyName}
              width={"100%"}
              required={required?.familyName}
            />
          </InputContainer>
          <InputContainer>
            <Input
              type="text"
              id="name"
              name="name"
              onChange={(e) => {
                handleChange && handleChange(e);
              }}
              value={name}
              label={regForm.name}
              width={"100%"}
              required={required?.name}
            />
          </InputContainer>
          <InputContainer>
            <Input
              type="text"
              id="surname"
              name="surname"
              onChange={(e) => {
                handleChange && handleChange(e);
              }}
              value={surname}
              label={regForm.surname}
              width={"100%"}
              required={required?.surname}
            />
          </InputContainer>
          <InputContainer>
            <Input
              type="text"
              id="company"
              name="company"
              onChange={(e) => {
                handleChange && handleChange(e);
              }}
              value={company}
              label={regForm.company}
              width={"100%"}
              required={required?.company}
            />
          </InputContainer>
          <InputContainer>
            <Input
              type="text"
              id="phone"
              name="phone"
              onChange={(e) => {
                handleChange && handleChange(e);
              }}
              value={phone}
              label={regForm.phone}
              width={"100%"}
              required={required?.phone}
            />
          </InputContainer>
          <InputContainer>
            <Input
              type={isPass1Visible ? "text" : "password"}
              id="password1"
              name="password1"
              onChange={handleChange}
              value={password1}
              label={regForm.password}
              help={getPassword1HelpText()}
              width={"100%"}
              required={required?.password}
              error={!!errors?.password1 && touched?.password1}
              onBlur={handleBlur}
            >
              <PasswordToggle isSelected={isPass1Visible} onClick={() => setIsPass1Visible(!isPass1Visible)} />
            </Input>
          </InputContainer>
          <InputContainer>
            <Input
              type={isPass2Visible ? "text" : "password"}
              id="password2"
              name="password2"
              onChange={(e) => {
                handleChange && handleChange(e);
                setIsPass2Changed(true);
              }}
              value={password2}
              label={regForm.confirmPassword}
              help={getPassword2HelpText()}
              width={"100%"}
              required={required?.password}
              error={!!errors?.password2 && isPass2Changed}
              onBlur={handleBlur}
            >
              <PasswordToggle isSelected={isPass2Visible} onClick={() => setIsPass2Visible(!isPass2Visible)} />
            </Input>
          </InputContainer>
        </>
      ) : (
        <InputContainer>
          <Textarea
            id="goals"
            name="goals"
            placeholder={t("goalsPlaceholder")}
            onChange={(e) => {
              handleChange && handleChange(e);
            }}
            value={goals}
            label={regForm.goals}
            width={"100%"}
            required={required?.goals}
          />
        </InputContainer>
      )}
    </>
  );
};

export const FormInputs = memo(FormInputsComponent);
